import {
  QUERY_PARAM_KEYS,
  QueryParams,
} from "@/definitions/types"
import { isValidDateRange } from "@/utils/date-validation"
import { endOfMonth, format, startOfMonth } from "date-fns"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useEffect } from "react"
import { toast } from "./use-toast"
import { REPORT_MAX_DAYS_DIFFERENCE } from "@/constants/forms-validation"

const ALLOWED_KEYS_SET = new Set(QUERY_PARAM_KEYS)

type AllowedQueryParamKeys =
  (typeof QUERY_PARAM_KEYS)[number]

const isValidQueryParamKey = (
  key: string
): key is AllowedQueryParamKeys => {
  return ALLOWED_KEYS_SET.has(key as AllowedQueryParamKeys)
}

export const useQueryParams = <T>(): QueryParams<T> => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const paramsObject: Partial<
    Record<AllowedQueryParamKeys, string | null>
  > = {}

  searchParams.forEach((value, key) => {
    if (isValidQueryParamKey(key)) {
      paramsObject[key] = value
    }
  })
  useEffect(() => {
    const paymentDateInit = searchParams.get(
      "paymentDateInit"
    )
    const paymentDateEnd = searchParams.get(
      "paymentDateEnd"
    )

    // Only validate if both dates exist
    if (paymentDateInit && paymentDateEnd) {
      const isValid = isValidDateRange(
        paymentDateInit,
        paymentDateEnd
      )

      if (!isValid) {
        // Create new URLSearchParams with corrected dates
        const newParams = new URLSearchParams(searchParams)
        const currentMonth = new Date()

        newParams.set(
          "paymentDateInit",
          format(startOfMonth(currentMonth), "yyyy-MM-dd")
        )
        newParams.set(
          "paymentDateEnd",
          format(endOfMonth(currentMonth), "yyyy-MM-dd")
        )

        // Update the URL
        navigate(
          `${window.location.pathname}?${newParams.toString()}`,
          { replace: true }
        )
        toast({
          title: "Período inválido",
          description: `O período de pagamento não pode ser maior que ${REPORT_MAX_DAYS_DIFFERENCE} dias`,
          variant: "destructive",
        })
      }
    }
  }, [navigate, searchParams])

  return paramsObject as QueryParams<T>
}
